import { Component, createRef } from 'react'
import { Platform, Text, View } from 'react-native'
import styled, { ThemeContext } from 'styled-components'
import PropTypes from 'prop-types'
import { Slider } from '../../../../elements/input/Slider/Slider'
import { media } from '../../../../style/media'
import { userSelectNone } from '../../../../style/webStyles.js'
import { formatTime } from '../../../../utils/secondsToDuration'
import { useSeekTime } from '../../Player/SeekTimeContext/SeekTimeContext'

const Wrapper = styled(View)`
  width: 100%;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  height: 32px;

  ${media.md`
    height: 56px;
  `}
`

const Time = styled(View)`
  flex-grow: 0;
  padding-left: ${(props) => (props.leftSide ? props.theme.spacing.md : 0)}px;
  padding-right: ${(props) => (props.rightSide ? props.theme.spacing.md : 0)}px;
`

// 16px margin to match native progress bar styles
const Progress = styled(View)`
  margin: 0 16px;
  flex-grow: 1;
`

const TimeText = styled(Text)`
  ${userSelectNone};
  text-align: center;
  color: ${(props) => props.theme.colors.text};
  font-size: ${(props) => props.theme.fontSizes.xsmall}px;
  ${media.sm`
    font-size: ${(props) => props.theme.fontSizes.small}px;
  `}
  ${media.md`
    font-size: ${(props) => props.theme.fontSizes.medium}px;
  `}
`

const trackTintColor = Platform.OS === 'android' ? 'white' : 'gray'

export class ProgressBar extends Component {
  static contextType = ThemeContext

  progressBarRef = createRef()

  state = {
    scrubbing: false,
    scrubValue: 0,
    progressBarWidth: 0,
    mouseOver: false,
  }

  static getDerivedStateFromProps(props, state) {
    // While not scrubbing timers reflect the current progress value
    if (!state.scrubbing && props.value !== state.scrubValue) {
      return { scrubValue: props.value }
    }
    return null
  }

  handleSlidingStart = () => {
    this.setState({ scrubbing: true })
    if (!this.state.scrubbing) {
      this.props.onSlidingStart?.()
    }
  }

  // This fires while scrubbing and sets the timers to the dragged slider position
  handleValueChange = (scrubValue) => {
    if (this.state.scrubbing) {
      this.setState({ scrubValue })
      const xPosition = (scrubValue / this.props.totalTime) * this.state.progressBarWidth
      this.props.onSlidingChange?.(scrubValue, xPosition)
    }
  }

  handleSlidingComplete = (finalValue) => {
    this.setState({ scrubbing: false, scrubValue: finalValue })
    this.props.onSlidingComplete(finalValue)
  }

  handleMouseEnter = () => this.setState({ mouseOver: true })

  handleMouseLeave = (ev) => {
    this.setState({ mouseOver: false })
    this.props.onMouseLeave?.(ev)
  }

  handleMouseMove = (ev) => {
    const rect = this.progressBarRef.current.getBoundingClientRect()
    const xPosition = ev.clientX - rect.left
    const xPercent = xPosition / rect.width
    this.props.onMouseMove?.(xPercent * this.props.totalTime, xPosition, ev)
  }

  handleLayout = (ev) => {
    this.setState({ progressBarWidth: ev.nativeEvent.layout.width })
    this.props.onLayout?.(ev)
  }

  render() {
    const {
      value,
      totalTime,
      scrubbingDisabled,
      progressTrackColor,
      inverted,
      tapToSeek,
      style,
      minimumFractionalDigits = 0,
      thickOnHover,
      children,
    } = this.props
    const { scrubbing, scrubValue, mouseOver } = this.state
    const theme = this.context
    return (
      <Wrapper style={style}>
        <Time leftSide>
          <TimeText>{formatTime(scrubValue || 0, true, minimumFractionalDigits)}</TimeText>
        </Time>
        <Progress
          ref={this.progressBarRef}
          onMouseMove={this.handleMouseMove}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          onLayout={this.handleLayout}
        >
          <Slider
            step={0.01}
            value={!scrubbing ? value : scrubValue}
            minimumValue={0}
            maximumValue={totalTime}
            minimumTrackTintColor={theme.colors[progressTrackColor ?? 'primary']}
            maximumTrackTintColor={theme.colors[trackTintColor] || trackTintColor}
            thumbTintColor={theme.colors.grayLighter}
            onValueChange={this.handleValueChange}
            onSlidingStart={this.handleSlidingStart}
            onSlidingComplete={this.handleSlidingComplete}
            scrubbingDisabled={scrubbingDisabled}
            inverted={inverted}
            tapToSeek={tapToSeek}
            className={thickOnHover && mouseOver ? 'rc-slider-wider' : undefined}
          />
          {children}
        </Progress>
        <Time rightSide>
          <TimeText>
            {formatTime(
              totalTime ? Math.max(0, totalTime - scrubValue) : 0,
              true,
              minimumFractionalDigits,
            )}
          </TimeText>
        </Time>
      </Wrapper>
    )
  }
}

const ConnectedProgressBar = (props) => {
  const { seekTime, totalTime } = useSeekTime()
  return <ProgressBar value={props.seekTime ?? seekTime} totalTime={totalTime} {...props} />
}

ConnectedProgressBar.propTypes = {
  onSlidingStart: PropTypes.func,
  onSlidingComplete: PropTypes.func.isRequired,
  /** Fires continuously as the slider is dragged */
  onSlidingChange: PropTypes.func,
}

export default ConnectedProgressBar
